import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Menu, X } from "lucide-react";

const NAV_ITEMS = [
  { label: "Mission", href: "#why" },
  { label: "Who we help", href: "#who-we-help" },
  { label: "Ways we help", href: "#ways-we-help" },
  { label: "Learning path", href: "#programs" },
  { label: "Membership", href: "#membership" },
  { label: "FAQ", href: "#faq" },
];

export const SiteHeader = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    if (window.location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors duration-300 ${
        scrolled
          ? "bg-background/90 backdrop-blur border-border/70"
          : "bg-background border-transparent"
      }`}
    >
      <div className="mx-auto w-full max-w-[1280px] px-5 sm:px-8">
        <div className="flex h-16 items-center justify-between">
          <a
            href="#hero"
            onClick={(e) => scrollTo(e, "#hero")}
            data-testid="header-logo-link"
            className="flex items-center"
          >
            <img
              src="https://customer-assets-39nsmqrw.emergentagent.net/job_pond-education-hub/artifacts/0b0oeg8t_logo%20%285%29.webp"
              alt="Pond & Aquaculture Resource Centre"
              className="h-9 sm:h-10 w-auto"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-6" aria-label="Primary">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollTo(e, item.href)}
                data-testid={`header-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}-link`}
                className="nav-underline text-[13px] font-medium text-foreground/75 hover:text-foreground"
              >
                {item.label}
              </a>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Button
              data-testid="header-cta-button"
              onClick={(e) => scrollTo(e, "#book")}
              className="btn-lift hidden sm:inline-flex rounded-none bg-[color:var(--apn-forest)] text-[color:var(--apn-paper-2)] hover:bg-[color:var(--apn-forest-2)] px-5 h-10 text-[13px] tracking-wide"
            >
              Book free consultation
            </Button>
            <button
              data-testid="header-mobile-menu-button"
              className="lg:hidden inline-flex items-center justify-center h-11 w-11 border border-border bg-card"
              onClick={() => setMobileOpen((v) => !v)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>
      </div>

      {mobileOpen && (
        <div data-testid="mobile-nav-menu" className="lg:hidden border-t border-border/70 bg-background">
          <nav className="mx-auto max-w-[1280px] px-5 py-4 flex flex-col gap-1" aria-label="Mobile">
            {NAV_ITEMS.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => scrollTo(e, item.href)}
                className="px-3 py-3 min-h-11 text-sm font-medium text-foreground/85 hover:bg-secondary border-b border-border/40 last:border-b-0"
              >
                {item.label}
              </a>
            ))}
            <Button
              data-testid="mobile-nav-cta-button"
              onClick={(e) => scrollTo(e, "#book")}
              className="btn-lift mt-3 rounded-none bg-[color:var(--apn-forest)] text-[color:var(--apn-paper-2)] hover:bg-[color:var(--apn-forest-2)] h-11"
            >
              Book free consultation
            </Button>
          </nav>
        </div>
      )}
    </header>
  );
};
